import { Suspense, useEffect, useState } from 'react'
import { Outlet, useMatches, useNavigate } from 'react-router-dom'
import { logoutUser } from '@services/auth'
import { toastQueue } from '@components/atoms/Toast'
import { HomeHeader } from '@components/shell/HomeHeader'
import {
  BottomFloatingNav,
  type NavItem,
} from '@components/shell/BottomFloatingNav'
import { PageTransition } from '@components/shell/PageTransition'
import { PageSkeleton } from '@components/shell/PageSkeleton'
import { ProfilePanel } from '@components/shell/ProfilePanel'
import { useSignedInUser } from '@hooks/useSignedInUser'
import { useTenantBranding } from '@hooks/useTenantBranding'
import { useTenantHash } from '@hooks/useTenantHash'
import type { AppRouteConfig } from '../../../routes/config'
import styles from './AppShell.module.scss'

// Bottom-nav items that drive both the floating pill and (via Router's
// `useLocation`-based active matching) the highlight state.
const navItems: NavItem[] = [
  { id: 'home', label: 'Tasks', icon: 'home', href: '/tasks' },
  { id: 'inbox', label: 'Inbox', icon: 'inbox-01', href: '/inbox' },
  {
    id: 'requests',
    label: 'Requests',
    icon: 'file-stack',
    href: '/engagements',
  },
]

/**
 * Layout chrome for every "list-style" route — `/`, `/tasks`,
 * `/engagements`, `/inbox`. Renders the HomeHeader at top, a scrollable
 * `<main>` landmark in the middle, and the BottomFloatingNav pill at
 * the bottom.
 *
 * NOTE: the ORO AI entry-point (OroAiButton + OroAiOverlay) was
 * previously wired into `BottomFloatingNav.trailingAction` and mounted
 * as a portal from here. It was removed before the first prod ship so
 * we can bring it back behind a proper feature flag rather than as a
 * scripted demo. The underlying components remain in-repo (see
 * `src/components/atoms/OroAiButton/` and
 * `src/components/shell/OroAiOverlay/`) — re-enabling is a pure JSX
 * re-wire, no rebuild required.
 */
export function AppShell() {
  const matches = useMatches()
  const navigate = useNavigate()
  const [isProfilePanelOpen, setProfilePanelOpen] = useState(false)
  const user = useSignedInUser()
  useTenantHash()
  const { branding, loading: brandingLoading } = useTenantBranding()

  async function handleLogout() {
    setProfilePanelOpen(false)
    try {
      await logoutUser()
    } catch {
      toastQueue.add(
        { message: 'Logout did not complete cleanly. Your local session was cleared anyway.', variant: 'warning' },
        { timeout: 4000 },
      )
    }
    navigate('/logout', { replace: true })
  }

  // Pull the leaf route's metadata to drive the document title.
  const handle = (matches[matches.length - 1]?.handle ?? null) as
    | AppRouteConfig
    | null

  useEffect(() => {
    document.title = handle?.title ? `${handle.title} · ORO` : 'ORO'
  }, [handle?.title])

  return (
    <div className={styles.shell}>
      {/* WCAG: skip-link is the first focusable element on every page. */}
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>

      <HomeHeader
        branding={branding}
        loading={brandingLoading}
        user={user}
        onProfileClick={() => setProfilePanelOpen(true)}
      />

      <main id="main-content" className={styles.main} tabIndex={-1}>
        {/* Inner Suspense — keeps the shell visible while a lazy page loads. */}
        <Suspense fallback={<PageSkeleton />}>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </Suspense>
      </main>

      <BottomFloatingNav items={navItems} />

      {/* Profile slide-in panel — portals to document.body, so its DOM
          position here is purely for component composition. */}
      <ProfilePanel
        open={isProfilePanelOpen}
        onClose={() => setProfilePanelOpen(false)}
        user={user}
        onLogout={handleLogout}
      />
    </div>
  )
}
